import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/autenticacao.jsx';
import { ROTULO_STATUS_REGISTRO, TOM_STATUS_REGISTRO } from '../../lib/coleta.js';
import { Aviso, Botao, Carregando, Etiqueta, Modal, Selecao } from '../../componentes/ui.jsx';
import { FormularioRegistro } from './FormularioRegistro.jsx';
import './Registros.css';

export function StatusRegistro({ valor }) {
  return <Etiqueta tom={TOM_STATUS_REGISTRO[valor] ?? 'neutro'}>{ROTULO_STATUS_REGISTRO[valor] ?? valor}</Etiqueta>;
}

export const dataISOParaBR = (iso) => iso.split('-').reverse().join('/');

/** O painel de uma OS / ficha: os campos, o status e o vinculo com a agenda e a conversa. */
export function DetalheRegistro({ id, modelo, aoFechar }) {
  const queryClient = useQueryClient();
  const { podeAcessar } = useAuth();
  const [valores, setValores] = useState(null);
  const [confirmarExclusao, setConfirmarExclusao] = useState(false);

  const registro = useQuery({ queryKey: ['registros', 'um', id], queryFn: () => api.get(`/api/registros/${id}`) });
  const r = registro.data?.registro;

  // O formulario comeca com o que esta gravado — e recomeca se a IA gravar
  // algo enquanto o painel esta aberto e ninguem mexeu ainda.
  const [mexeu, setMexeu] = useState(false);
  useEffect(() => {
    if (r && !mexeu) setValores(r.dados);
  }, [r, mexeu]);

  const atualizar = () => queryClient.invalidateQueries({ queryKey: ['registros'] });
  const salvar = useMutation({
    mutationFn: (mudancas) => api.patch(`/api/registros/${id}`, mudancas),
    onSuccess: () => {
      setMexeu(false);
      atualizar();
    }
  });
  // O status salva na hora e nao mexe no que esta sendo digitado nos campos.
  const mudarStatus = useMutation({
    mutationFn: (status) => api.patch(`/api/registros/${id}`, { status }),
    onSuccess: atualizar
  });
  const excluir = useMutation({
    mutationFn: () => api.delete(`/api/registros/${id}`),
    onSuccess: () => {
      atualizar();
      aoFechar();
    }
  });

  function mudar(chave, valor) {
    setMexeu(true);
    setValores((v) => ({ ...v, [chave]: valor }));
  }

  /** So o que mudou vai para a API; campo esvaziado vai como null (limpa). */
  function diferencas() {
    const mudou = {};
    for (const c of modelo.campos) {
      const antes = r.dados[c.chave] ?? '';
      const depois = valores[c.chave] ?? '';
      if (antes !== depois) mudou[c.chave] = depois === '' ? null : depois;
    }
    return mudou;
  }

  return (
    <Modal
      titulo={r ? `${r.codigo} · ${r.leadNome ?? ''}` : modelo.rotulo}
      aberto
      aoFechar={aoFechar}
      largura={760}
      rodape={
        r && (
          <>
            {podeAcessar('admin') && (
              <Botao variante="perigo" onClick={() => (confirmarExclusao ? excluir.mutate() : setConfirmarExclusao(true))} carregando={excluir.isPending}>
                {confirmarExclusao ? 'Confirmar exclusão' : 'Excluir'}
              </Botao>
            )}
            <span style={{ flex: 1 }} />
            <Botao variante="secundario" onClick={aoFechar}>
              Fechar
            </Botao>
            <Botao disabled={!mexeu} carregando={salvar.isPending} onClick={() => salvar.mutate({ dados: diferencas() })}>
              Salvar
            </Botao>
          </>
        )
      }
    >
      {registro.isError ? (
        <Aviso tom="perigo">{registro.error.message}</Aviso>
      ) : registro.isLoading || !valores ? (
        <Carregando />
      ) : (
        <div className="coluna">
          {(salvar.isError || mudarStatus.isError || excluir.isError) && (
            <Aviso tom="perigo">{(salvar.error ?? mudarStatus.error ?? excluir.error).message}</Aviso>
          )}

          <div className="reg-cabeca">
            <div className="reg-cabeca__status">
              <span className="campo__rotulo">Status</span>
              <Selecao value={r.status} onChange={(e) => mudarStatus.mutate(e.target.value)}>
                {Object.entries(ROTULO_STATUS_REGISTRO).map(([v, rot]) => (
                  <option key={v} value={v}>
                    {rot}
                  </option>
                ))}
              </Selecao>
            </div>
            <div>
              <span className="campo__rotulo">Dados obrigatórios</span>
              <p>
                <strong>
                  {r.progresso.preenchidosObrigatorios} de {r.progresso.obrigatorios}
                </strong>
                {!r.progresso.completo && <span className="texto-suave"> · falta: {r.progresso.faltam.map((c) => c.rotulo).join(', ')}</span>}
              </p>
            </div>
            <div>
              <span className="campo__rotulo">Horário na agenda</span>
              <p>
                {r.agendamento ? (
                  <>
                    {dataISOParaBR(r.agendamento.data)} às {r.agendamento.hora}
                    {r.agendamento.servicoNome && ` · ${r.agendamento.servicoNome}`}
                    {r.agendamento.profissionalNome && ` com ${r.agendamento.profissionalNome}`}
                  </>
                ) : (
                  <span className="texto-suave">Sem horário marcado</span>
                )}
              </p>
            </div>
            {r.conversationId && (
              <Link to={`/conversas?id=${r.conversationId}`} className="reg-link">
                Abrir a conversa →
              </Link>
            )}
          </div>

          <FormularioRegistro campos={modelo.campos} valores={valores} origem={r.origemCampos} aoMudar={mudar} />
        </div>
      )}
    </Modal>
  );
}

